import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { WealthboxStatus } from './wealthbox.service';

/**
 * Backend status + identity surface for the /settings page. Each call hits a
 * cheap, owner-scoped status endpoint. Designed to be called on page load and
 * refreshed after the user clicks a "sync now" button.
 */
@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl.replace(/\/+$/, '');

  async me(): Promise<MeResponse> {
    return await firstValueFrom(this.http.get<MeResponse>(`${this.apiBase}/api/me`));
  }

  async wealthboxStatus(): Promise<WealthboxStatus> {
    return await firstValueFrom(this.http.get<WealthboxStatus>(`${this.apiBase}/api/wealthbox/status`));
  }

  async calendarStatus(): Promise<CalendarStatus> {
    return await firstValueFrom(this.http.get<CalendarStatus>(`${this.apiBase}/api/calendar/status`));
  }

  async scanCalendar(lookAheadHours = 48): Promise<CalendarScanResult> {
    return await firstValueFrom(
      this.http.post<CalendarScanResult>(
        `${this.apiBase}/api/calendar/scan?lookAheadHours=${lookAheadHours}`,
        {}));
  }

  async usageSummary(days = 7): Promise<UsageSummary> {
    return await firstValueFrom(
      this.http.get<UsageSummary>(`${this.apiBase}/api/usage/summary?days=${days}`));
  }

  /**
   * Today's spend vs the configured daily soft cap. Drives the budget
   * banner — yellow at 75%+, red at 100%+. Cheap enough to poll on a few-minute
   * cadence; the response is computed on demand and not cached server-side.
   */
  async budget(): Promise<BudgetResponse> {
    return await firstValueFrom(
      this.http.get<BudgetResponse>(`${this.apiBase}/api/usage/budget`));
  }
}

export interface MeResponse {
  entraObjectId: string;
  displayName: string;
  email: string;
  tenantId: string;
  serverTimeUtc: string;
}

export interface CalendarStatus {
  lastScannedAt: string | null;
  upcomingPrepCount: number;
}

export interface CalendarScanResult {
  ranAt: string;
  eventsScanned: number;
  prepActionsCreated: number;
  prepActionsUpdated: number;
  skipped: number;
  errors: number;
}

export interface UsageSummary {
  fromDate: string;
  toDate: string;
  today: UsageWindow;
  period: UsageWindow;
  byOperation: OperationBreakdown[];
}

export interface UsageWindow {
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  estimatedCostUsd: number;
}

export interface OperationBreakdown {
  operation: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
}

export type BudgetStatus = 'ok' | 'warning' | 'exceeded';

export interface BudgetResponse {
  date: string;
  spentUsd: number;
  capUsd: number;
  percentUsed: number;
  status: BudgetStatus;
}
